import React, { useState } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Image,
  InputGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { authService } from '../services/authService';
import { accountService } from '../services/accountService';
import urlImage from "../assets/images/auth-image.png";
import urlTrackingIcon from "../assets/images/icons/url-link.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import cameraIcon from "../assets/images/icons/cameraIcon.png";
import editImage from "../assets/images/icons/edit-image.png";
import uplaodDafaultPhoto from "../assets/images/icons/uploadPhoto.png";


const SignUp = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [accountName, setAccountName] = useState("");
  const [contactNo, setcontactNo] = useState("");
  const [location, setLocation] = useState("");
  const [language, setLanguage] = useState("english");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    accountName: '',
    contactNo: '',
    location: '',
    logo: ''
  });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/(?=.*\d)/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/(?=.*[!@#$%^&*])/.test(password)) {
      return "Password must contain at least one special character (!@#$%^&*)";
    }
    return "";
  };

  const validateContactNo = (number) => {
    const phoneRegex = /^\d{10}$/;
    if (!number.trim()) {
      return "Contact number is required";
    }
    if (!phoneRegex.test(number)) {
      return "Contact number must be exactly 10 digits";
    }
    return "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors(prev => ({ ...prev, logo: "Please upload an image file (JPG, PNG, or GIF)" }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: "File size should be less than 5MB" }));
        return;
      }

      setLogo(file);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      setErrors(prev => ({ ...prev, logo: "" }));
    }
  };

  const uploadFile = async () => {
    if (!logo) return null;

    const formData = new FormData();
    formData.append("file", logo);

    try {
      const response = await accountService.uploadFile(formData);

      setUploadProgress(0);
      return response.data;
    } catch (error) {
      throw new Error("Failed to upload logo");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    
    const newErrors = {
      email: validateEmail(email),
      password: validatePassword(password),
      confirmPassword: password !== confirmPassword ? "Passwords do not match" : "",
      accountName: !accountName.trim() ? "Account name is required" : accountName.length > 20 ? "Account name must not exceed 20 characters" : "",
      contactNo: validateContactNo(contactNo),
      location: !location.trim() ? "Location is required" : "",
    };

    const hasErrors = Object.values(newErrors).some(error => error !== "");
    setErrors(newErrors);

    if (hasErrors) {
      return;
    }

    try {
      let logoFileName = null;
      if (logo) {
        logoFileName = await uploadFile();
      }

      const response = await authService.register({
        accountName,
        password,
        email,
        contactNo,
        location,
        language,
        logo: logoFileName,
      });

      if (response) {
        try {
          const verificationResponse = await authService.sendEmailVerification(email);

          if (
            verificationResponse.success === false &&
            verificationResponse.message === "Email is already verified"
          ) {
            setSuccess("Email is already verified. Redirecting to login page");
            setTimeout(() => {
              navigate("/auth/login");
            }, 2000);
          } else {
            navigate("/auth/verify-email", { state: { email } });
          }
        } catch (verificationErr) {
          setError("Account created but failed to send verification email. Please try logging in.");
        }
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError("An account with this email already exists");
      } else if (err.response?.status === 400) {
        setError(err.response.data.message || "Invalid input data. Please check your information.");
      } else if (!navigator.onLine) {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError("Registration failed. Please try again later.");
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prev => !prev);
  };

  return (
    <section className="pt-5 pb-5">
      <Container fluid className="vh-100">
        <Row className="h-100 justify-content-center">
          <Col lg={4} md={12} sm={12} className="d-none d-md-block">
            <div
              className="h-100 w-100"
              style={{
                backgroundImage: `url(${urlImage})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
          </Col>
          <Col
            lg={6}
            md={12}
            sm={12}
            className="d-flex justify-content-center align-items-center"
          >
            <Card className="p-4 auth-form-card auth-form-card-two shadow br-radius-12 mb-5">
              <div className="text-start mb-3">
                <Button 
                  variant="link" 
                  onClick={handleBack}
                  className="text-dark p-0"
                  style={{ textDecoration: 'none' }}
                >
                  ←
                </Button>
              </div>
              <div className="text-center mb-4">
                <h5 className="GeneralSansSemibold pt-3 fs-20">
                  URL Shortening.
                </h5>
                <p className="mb-0 fs-14">
                  Please fill the below details to create your account
                </p>
              </div>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              <Form onSubmit={handleSubmit} noValidate>
                <Row>
                  <Col lg={12} className="text-center mb-4">
                    <Form.Group controlId="formLogo">
                      <div
                        className="position-relative m-auto z-0"
                        style={{ width: "100px" }}
                      >
                        <div
                          className="bg-light d-flex align-items-center justify-content-center position-relative mb-3"
                          style={{
                            width: "100px",
                            height: "100px",
                            border: "3px solid #F2F4F9",
                            borderRadius: "50%",
                            padding: "3px",
                            overflow: "hidden",
                          }}
                        >
                          {logoPreview ? (
                            <Image
                              src={logoPreview}
                              className="w-100"
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: "50%",
                                padding: "0px",
                                objectFit:'cover'
                              }}
                            />
                          ) : (
                            <div
                              className="bg-light d-flex align-items-center justify-content-center position-relative"
                              style={{
                                // border: "3px solid #F2F4F9",
                                borderRadius: "50%",
                                overflow: "hidden",
                              }}
                            >
                              <Image
                                src={uplaodDafaultPhoto}
                                className="w-100"
                              />
                            </div>
                          )}
                          {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="position-absolute top-50 start-50 translate-middle">
                              <div className="text-center text-primary">
                                {uploadProgress}%
                              </div>
                            </div>
                          )}
                        </div>
                        {/* <Form.Label className="cameraIcon mt-2">
                      {logo ? 'Change Logo' : 'Upload Logo'}
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </Form.Label> */}
                        <Form.Label
                          className="cameraIcon mt-2"
                          style={{ cursor: "pointer" }}
                        >
                          <img
                            className="w-100"
                            src={logo ? editImage : cameraIcon}
                            // alt={logo ? 'Change Logo' : 'Upload Logo'}
                          />
                          {/* {logo ? 'Change Logo' : 'Upload Logo'} */}
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                        </Form.Label>
                      </div>
                    </Form.Group>
                    {errors.logo && <div className="text-danger text-center mb-3 fs-12">{errors.logo}</div>}
                  </Col>
                  <Col lg={6}>
                    <Form.Group
                      controlId="formBasicAccountName"
                      className="mb-3"
                    >
                      <Form.Label className="fs-12">Account Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter account name"
                        value={accountName}
                        onChange={(e) => setAccountName(e.target.value)}
                        isInvalid={!!errors.accountName}
                        maxLength={20}
                        required
                      />
                      <Form.Control.Feedback type="invalid">{errors.accountName}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group controlId="formBasicEmail" className="mb-3">
                      <Form.Label className="fs-12">Email address <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        isInvalid={!!errors.email}
                        required
                      />
                      <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group controlId="formBasicPassword" className="mb-3">
                      <Form.Label className="fs-12">Password <span className="text-danger">*</span></Form.Label>
                                      <div className="position-relative">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          isInvalid={!!errors.password}
                          required
                                              className="pe-5"
                        />
                        <span
className="position-absolute top-50 end-3 right-3 translate-middle-y fs-12 text-success"
style={{right:`${showPassword ? '10px' : '10px'}`}}                          
onClick={togglePasswordVisibility}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </span>
                      </div>
                      <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group
                      controlId="formBasicConfirmPassword"
                      className="mb-3"
                    >
                      <Form.Label className="fs-12">
                        Confirm Password <span className="text-danger">*</span>
                      </Form.Label>
          <div className="position-relative">
                        <Form.Control
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          isInvalid={!!errors.confirmPassword}
                          required
                          className="pe-5"
                        />
 <span
className="position-absolute top-50 end-3 right-3 translate-middle-y fs-12 text-success"
                          onClick={toggleConfirmPasswordVisibility}
                          aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                          style={{right:`${showConfirmPassword ? '10px' : '10px'}`}}
                        >
                          <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                        </span>
                      </div>
                      <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                <Col lg={6}>
                  <Form.Group controlId="formBasicLocation" className="mb-3">
                    <Form.Label className="fs-12">Location <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      isInvalid={!!errors.location}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.location}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={6}>
                  <Form.Group controlId="formBasicLanguage" className="mb-3">
                    <Form.Label className="fs-12">Language <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter preferred language"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col lg={12}>
                  <Form.Group controlId="formBasicContactNumber" className="mb-3">
                    <Form.Label className="fs-12">Contact Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter contact number"
                      value={contactNo}
                      onChange={(e) => setcontactNo(e.target.value)}
                      isInvalid={!!errors.contactNo}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{errors.contactNo}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={12}>
                  <Button variant="success" type="submit" className="w-100"> 
                    Sign up
                  </Button>
                  <p className="fs-12 mt-3 text-center">Already have an account? <a className="text-dark" href="/auth/login">Login here</a></p>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
    </section>
  );
};

export default SignUp;
